import type { ScoringRules, TemplateContent, TemplateItem } from './templates';
import { allItems } from './templates';

export const RESULTS = ['pass', 'fail', 'na', 'pending'] as const;
export type Result = (typeof RESULTS)[number];

export type ResponseLike = { item_key: string; result: Result };

export type ScoreSummary = {
  score: number;
  total_items: number;
  answered: number;
  passed: number;
  failed: number;
  na: number;
  section_scores: Record<string, number>;
};

// Guide 13.1: Score = round(Passed / (Answered - N/A) * 100). N/A and unanswered items
// are left out of the denominator; 0 when nothing scorable is answered. With weighted
// scoring on, each item counts by its points instead of 1.
function scoreOf(items: TemplateItem[], byKey: Map<string, Result>, weighted: boolean) {
  let earned = 0;
  let possible = 0;
  let passed = 0;
  let failed = 0;
  let na = 0;
  for (const item of items) {
    const r = byKey.get(item.key) ?? 'pending';
    const w = weighted ? item.points : 1;
    if (r === 'pass') {
      passed++;
      earned += w;
      possible += w;
    } else if (r === 'fail') {
      failed++;
      possible += w;
    } else if (r === 'na') {
      na++;
    }
  }
  const score = possible > 0 ? Math.round((earned / possible) * 100) : 0;
  return { score, passed, failed, na };
}

export function computeScore(content: Pick<TemplateContent, 'sections' | 'scoring_rules'>, responses: ResponseLike[]): ScoreSummary {
  const byKey = new Map(responses.map((r) => [r.item_key, r.result]));
  const weighted = content.scoring_rules.use_weighted_scoring;
  const items = allItems(content);
  const overall = scoreOf(items, byKey, weighted);
  const section_scores: Record<string, number> = {};
  for (const s of content.sections) section_scores[s.key] = scoreOf(s.items, byKey, weighted).score;
  return {
    score: overall.score,
    total_items: items.length,
    answered: overall.passed + overall.failed + overall.na,
    passed: overall.passed,
    failed: overall.failed,
    na: overall.na,
    section_scores,
  };
}

export type StoreStatus = 'passing' | 'warning' | 'failing';

export function statusFor(score: number, rules: Pick<ScoringRules, 'passing_threshold' | 'warning_threshold'>): StoreStatus {
  if (score >= rules.passing_threshold) return 'passing';
  if (score >= rules.warning_threshold) return 'warning';
  return 'failing';
}

// Temperature items: inside [min, max] passes, outside fails. A missing bound is open-ended.
export function evaluateTemperature(value: number, item: Pick<TemplateItem, 'temp_min' | 'temp_max'>): 'pass' | 'fail' {
  if (item.temp_min != null && value < item.temp_min) return 'fail';
  if (item.temp_max != null && value > item.temp_max) return 'fail';
  return 'pass';
}

export function describeRange(item: Pick<TemplateItem, 'temp_min' | 'temp_max' | 'temp_unit'>): string {
  const u = `°${item.temp_unit}`;
  if (item.temp_min != null && item.temp_max != null) return `${item.temp_min}–${item.temp_max}${u}`;
  if (item.temp_min != null) return `≥ ${item.temp_min}${u}`;
  if (item.temp_max != null) return `≤ ${item.temp_max}${u}`;
  return 'any';
}

// Recurring-failure alert tiers (guide 6.4): 1–2, 3–4 and 5+ prior failures.
export type RecurrenceTier = 'none' | 'low' | 'medium' | 'high';
export function recurrenceTier(priorFailures: number): RecurrenceTier {
  if (priorFailures >= 5) return 'high';
  if (priorFailures >= 3) return 'medium';
  if (priorFailures >= 1) return 'low';
  return 'none';
}
